<template>
  <div class="franchise-locations-panel" :class="{ embedded }">
    <div v-if="!embedded && syncInfo" class="sync-banner" :class="syncStatusClass">
      <i class="fas fa-sync-alt"></i>
      FranConnect {{ syncInfo.environment }} sync:
      {{ syncInfo.last_sync_status || 'unknown' }}
      <span v-if="syncInfo.last_sync_at"> · {{ formatDateTime(syncInfo.last_sync_at) }}</span>
    </div>

    <div class="filters-section">
      <div v-if="showProjectInput" class="filter-group">
        <label for="project-id">Project ID:</label>
        <input
          id="project-id"
          v-model.number="localProjectId"
          type="number"
          min="1"
          @change="onProjectChange"
        />
      </div>
      <div class="filter-group">
        <label for="search">Search:</label>
        <input
          id="search"
          v-model="filters.search"
          type="text"
          placeholder="Store #, entity, city, state"
          @input="debouncedFetch"
        />
      </div>
      <div class="filter-group">
        <label for="grain-filter">View by:</label>
        <select id="grain-filter" v-model="filters.grain" @change="resetAndFetch">
          <option value="store">Store # (FranConnect)</option>
          <option value="franchisee">Franchisee / entity (ADI)</option>
        </select>
      </div>
      <div class="filter-group">
        <label for="preset-filter">Report Preset:</label>
        <select id="preset-filter" v-model="filters.reportPreset" @change="onPresetChange">
          <option value="">All Active (sync)</option>
          <option value="jmfs_fc_roster">JMFS FC roster (Open + In Development)</option>
          <option value="jmfs_coi_verify">JMFS ADI (Open + In Permit / In Construction)</option>
          <option value="jmfs_open_needs_insurance">JMFS Open — needs insurance (~3400 stores)</option>
          <option value="jmfs_needs_insurance">JMFS Any lifecycle — needs insurance</option>
          <option value="jmfs_active">JMFS Active (Open + In Dev + Transfers)</option>
          <option value="jmfs_insurance">JMFS Insurance Eligible</option>
        </select>
      </div>
      <div class="filter-group">
        <label for="lifecycle-filter">Lifecycle Stage:</label>
        <select
          id="lifecycle-filter"
          v-model="filters.lifecycleStage"
          :disabled="Boolean(filters.reportPreset)"
          @change="resetAndFetch"
        >
          <option value="">All</option>
          <option v-for="stage in lifecycleOptions" :key="stage" :value="stage">
            {{ stage }}
          </option>
        </select>
      </div>
      <div class="filter-group">
        <label for="legal-filter">Legal Status:</label>
        <select id="legal-filter" v-model="filters.legalStatus" @change="resetAndFetch">
          <option value="">All</option>
          <option value="none">(none)</option>
          <option v-for="status in legalStatusOptions" :key="status" :value="status">
            {{ status }}
          </option>
        </select>
      </div>
      <div class="filter-group">
        <label for="store-status-filter">Store Status:</label>
        <select id="store-status-filter" v-model="filters.storeStatus" @change="resetAndFetch">
          <option value="">All</option>
          <option value="none">(none)</option>
          <option v-for="status in storeStatusOptions" :key="status" :value="status">
            {{ status }}
          </option>
        </select>
      </div>
      <div class="filter-group">
        <label for="state-filter">State:</label>
        <select id="state-filter" v-model="filters.state" @change="resetAndFetch">
          <option value="">All</option>
          <option v-for="st in stateOptions" :key="st" :value="st">
            {{ st }}
          </option>
        </select>
      </div>
      <div class="filter-group">
        <label for="dev-filter">Dev Status:</label>
        <select
          id="dev-filter"
          v-model="filters.devStatus"
          :disabled="Boolean(filters.reportPreset)"
          @change="resetAndFetch"
        >
          <option value="">All</option>
          <option value="none">(none)</option>
          <option v-for="dev in devStatusOptions" :key="dev" :value="dev">
            {{ dev }}
          </option>
        </select>
      </div>
      <button class="refresh-btn" :disabled="loading" @click="refreshData">
        <i class="fas fa-sync-alt" :class="{ 'fa-spin': loading }"></i>
        Refresh
      </button>
      <button
        class="refresh-btn export-btn"
        :disabled="loading || totalCount === 0"
        title="Downloads exactly the rows shown for your current Report Preset and filters."
        @click="exportToCSV"
      >
        <i class="fas fa-file-export"></i>
        Export current view
      </button>
    </div>

    <div v-if="summary && showStats" class="stats-cards">
      <div class="stat-card stat-card-highlight">
        <div class="stat-value">{{ primaryStatValue }}</div>
        <div class="stat-label">{{ primaryStatLabel }}</div>
      </div>
      <div class="stat-card">
        <div class="stat-value">{{ totalCount }}</div>
        <div class="stat-label">{{ matchingRowsLabel }}</div>
      </div>
      <div class="stat-card">
        <div class="stat-value">{{ tertiaryStatValue }}</div>
        <div class="stat-label">{{ tertiaryStatLabel }}</div>
      </div>
      <div v-if="isStoreGrain && cvOpenSyncedCount !== fcOpenStoreCount" class="stat-card">
        <div class="stat-value">{{ cvOpenSyncedCount }}</div>
        <div class="stat-label">Open synced in ADI (drift)</div>
      </div>
      <div class="stat-card">
        <div class="stat-value">{{ secondaryStatValue }}</div>
        <div class="stat-label">{{ secondaryStatLabel }}</div>
      </div>
    </div>

    <div v-if="loading && locations.length === 0" class="loading-state">
      <i class="fas fa-spinner fa-spin"></i>
      <p>Loading active locations...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <i class="fas fa-exclamation-triangle"></i>
      <p>{{ error }}</p>
      <button class="retry-btn" @click="fetchData">Try Again</button>
    </div>

    <div v-else-if="locations.length > 0" class="table-container">
      <div class="table-header">
        <h2>{{ tableTitle }}</h2>
        <span class="result-count">
          {{ tableSubtitle }} Showing {{ pagination.offset + 1 }}–{{ Math.min(pagination.offset + locations.length, totalCount) }}
          of {{ totalCount }}
        </span>
      </div>
      <div class="table-wrapper">
        <table class="locations-table">
          <thead>
            <tr>
              <th>Store #</th>
              <th>FC Ref</th>
              <th>Legal Entity</th>
              <th>City</th>
              <th>State</th>
              <th>Lifecycle</th>
              <th>In Dev</th>
              <th>Dev Status</th>
              <th>WC</th>
              <th>COI</th>
              <th>Compliance</th>
              <th v-if="entityLinks">Record</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="loc in locations" :key="rowKey(loc)">
              <td class="store-number">{{ loc.store_number }}</td>
              <td class="fc-ref">{{ loc.fc_franchisee_no || '—' }}</td>
              <td class="entity-name">{{ loc.legal_entity_name }}</td>
              <td>{{ loc.city || '—' }}</td>
              <td>{{ loc.state || '—' }}</td>
              <td>
                <span class="badge" :class="lifecycleBadgeClass(loc.lifecycle_stage)">
                  {{ loc.lifecycle_stage || '—' }}
                </span>
              </td>
              <td>
                <span v-if="loc.in_development" class="badge badge-warning">In Dev</span>
                <span v-else>—</span>
              </td>
              <td>{{ loc.dev_status || '—' }}</td>
              <td>
                <span v-if="loc.needs_workers_comp" class="badge badge-warning">WC</span>
                <span v-else>—</span>
              </td>
              <td>
                <span v-if="loc.needs_construction_insurance" class="badge badge-warning">COI</span>
                <span v-else>—</span>
              </td>
              <td>{{ loc.compliance_status }}</td>
              <td v-if="entityLinks">
                <router-link
                  v-if="loc.subject_id"
                  :to="`/projects/${localProjectId}/entities/${loc.subject_id}`"
                  class="entity-link"
                >
                  Open
                </router-link>
                <span v-else>—</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="pagination.has_more || pagination.offset > 0" class="pagination">
        <button
          class="page-btn"
          :disabled="loading || pagination.offset === 0"
          @click="goToPage(pagination.offset - pagination.limit)"
        >
          <i class="fas fa-chevron-left"></i> Previous
        </button>
        <span class="page-info">Page {{ currentPage }} of {{ totalPages }}</span>
        <button
          class="page-btn"
          :disabled="loading || !pagination.has_more"
          @click="goToPage(pagination.offset + pagination.limit)"
        >
          Next <i class="fas fa-chevron-right"></i>
        </button>
      </div>
    </div>

    <div v-else class="empty-state">
      <i class="fas fa-store-slash"></i>
      <h3>No active locations found</h3>
      <p>Run a FranConnect sync for project {{ localProjectId }} or adjust your filters.</p>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted } from 'vue'
import { debounce } from 'lodash-es'
import { saveAs } from 'file-saver'
import { UI_CONSTANTS } from '@/constants'
import api from '@/services/api'

const props = defineProps({
  projectId: { type: Number, default: 13 },
  embedded: { type: Boolean, default: false },
  showProjectInput: { type: Boolean, default: false },
  showStats: { type: Boolean, default: true },
  entityLinks: { type: Boolean, default: false },
})

const emit = defineEmits(['loaded', 'sync-info'])

const loading = ref(false)
const error = ref(null)
const localProjectId = ref(props.projectId)
const locations = ref([])
const summary = ref(null)
const syncInfo = ref(null)
const pagination = reactive({
  total: 0,
  limit: UI_CONSTANTS.DEFAULT_PAGE_SIZE * 5,
  offset: 0,
  has_more: false,
})

const filters = reactive({
  search: '',
  lifecycleStage: '',
  legalStatus: '',
  storeStatus: '',
  state: '',
  devStatus: '',
  reportPreset: 'jmfs_fc_roster',
  grain: 'store',
})

watch(() => props.projectId, (val) => {
  localProjectId.value = val
  resetAndFetch()
})

const totalCount = computed(() => pagination.total || 0)
const currentPage = computed(() => Math.floor(pagination.offset / pagination.limit) + 1)
const totalPages = computed(() => Math.max(1, Math.ceil(totalCount.value / pagination.limit)))

const lifecycleOptions = computed(() => Object.keys(summary.value?.by_lifecycle_stage || {}))
const stateOptions = computed(() => Object.keys(summary.value?.by_state || {}).filter((s) => s !== 'Unknown'))
const devStatusOptions = computed(() => Object.keys(summary.value?.by_dev_status || {}).filter((s) => s !== '(none)'))
const legalStatusOptions = computed(() => Object.keys(summary.value?.by_legal_status || {}).filter((s) => s !== '(none)'))
const storeStatusOptions = computed(() => Object.keys(summary.value?.by_store_status || {}).filter((s) => s !== '(none)'))

const isStoreGrain = computed(() => filters.grain !== 'franchisee')
const fcOpenStoreCount = computed(() => (
  summary.value?.fc_open_store_numbers_at_sync
  ?? summary.value?.open_store_numbers
  ?? 0
))
const inDevelopmentStoreCount = computed(() => (
  summary.value?.fc_in_development_store_numbers_at_sync
  ?? summary.value?.fc_in_development_store_numbers
  ?? 0
))
const fcRosterStoreCount = computed(() => (
  summary.value?.fc_roster_store_numbers_at_sync
  ?? summary.value?.fc_roster_store_numbers
  ?? 0
))
const cvOpenSyncedCount = computed(() => summary.value?.cv_open_store_numbers_synced ?? 0)
const activeSubjectCount = computed(() => summary.value?.active_subjects ?? 0)
const primaryStatValue = computed(() => (isStoreGrain.value ? fcOpenStoreCount.value : activeSubjectCount.value))
const primaryStatLabel = computed(() => (
  isStoreGrain.value ? 'FC Open store #s (source of truth)' : 'Active franchisees (entities)'
))
const secondaryStatValue = computed(() => (isStoreGrain.value ? inDevelopmentStoreCount.value : fcOpenStoreCount.value))
const secondaryStatLabel = computed(() => (
  isStoreGrain.value ? 'In Development store #s (flagged)' : 'FC Open store #s'
))
const tertiaryStatValue = computed(() => (isStoreGrain.value ? fcRosterStoreCount.value : inDevelopmentStoreCount.value))
const tertiaryStatLabel = computed(() => (
  isStoreGrain.value ? 'FC roster (Open + In Dev)' : 'In Development store #s'
))
const matchingRowsLabel = computed(() => (
  isStoreGrain.value ? 'Matching store rows' : 'Matching franchisee rows'
))
const tableTitle = computed(() => (
  isStoreGrain.value ? 'Active store locations' : 'Active franchisees'
))
const tableSubtitle = computed(() => (
  isStoreGrain.value
    ? 'One row per store #.'
    : 'One row per franchisee entity (representative store shown).'
))

const syncStatusClass = computed(() => {
  if (!syncInfo.value) return ''
  if (syncInfo.value.last_sync_status === 'success') return 'sync-success'
  if (syncInfo.value.last_sync_status === 'error') return 'sync-error'
  return 'sync-neutral'
})

function buildParams(includePagination = true) {
  const params = new URLSearchParams()
  params.append('project_id', String(localProjectId.value))
  if (filters.search) params.append('search', filters.search)
  if (filters.lifecycleStage) params.append('lifecycle_stage', filters.lifecycleStage)
  if (filters.reportPreset) params.append('report_preset', filters.reportPreset)
  if (filters.grain) params.append('grain', filters.grain)
  if (filters.legalStatus) params.append('legal_status', filters.legalStatus)
  if (filters.storeStatus) params.append('store_status', filters.storeStatus)
  if (filters.state) params.append('state', filters.state)
  if (filters.devStatus) params.append('dev_status', filters.devStatus)
  if (includePagination) {
    params.append('limit', String(pagination.limit))
    params.append('offset', String(pagination.offset))
  }
  return params
}

async function fetchData() {
  loading.value = true
  error.value = null
  try {
    const response = await api.get(`/franconnect/active-locations-report/?${buildParams()}`)
    const result = response.data
    if (!result.success) {
      error.value = result.error || 'Failed to load active locations report'
      return
    }
    const data = result.data || {}
    locations.value = data.locations || []
    summary.value = data.summary || null
    syncInfo.value = data.franconnect_sync || null
    emit('sync-info', syncInfo.value)
    emit('loaded', { summary: summary.value, total: data.pagination?.total || 0 })

    const page = data.pagination || {}
    pagination.total = page.total || 0
    pagination.limit = page.limit || pagination.limit
    pagination.offset = page.offset || 0
    pagination.has_more = Boolean(page.has_more)
  } catch (err) {
    error.value = err.response?.data?.error || err.message || 'Network error'
  } finally {
    loading.value = false
  }
}

const debouncedFetch = debounce(() => {
  pagination.offset = 0
  fetchData()
}, UI_CONSTANTS.SEARCH_DEBOUNCE_MS)

function onPresetChange() {
  if (filters.reportPreset) {
    filters.lifecycleStage = ''
    filters.devStatus = ''
  }
  resetAndFetch()
}

function onProjectChange() {
  resetAndFetch()
}

function resetAndFetch() {
  pagination.offset = 0
  fetchData()
}

function refreshData() {
  fetchData()
}

function goToPage(offset) {
  pagination.offset = Math.max(0, offset)
  fetchData()
}

async function exportToCSV() {
  try {
    const params = buildParams(false)
    params.append('export', 'csv')
    const response = await api.get(`/franconnect/active-locations-report/?${params}`, {
      responseType: 'blob',
    })
    const presetSuffix = filters.reportPreset ? `-${filters.reportPreset}` : ''
    const grainSuffix = filters.grain && filters.grain !== 'store' ? `-${filters.grain}` : ''
    const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' })
    const ts = new Date().toISOString().replace(/[:T]/g, '-').slice(0, 19)
    saveAs(blob, `active-locations-project-${localProjectId.value}${presetSuffix}${grainSuffix}-${ts}.csv`)
  } catch (err) {
    error.value = err.response?.data?.error || 'CSV export failed'
  }
}

function rowKey(loc) {
  if (isStoreGrain.value && loc.location_id) {
    return `loc-${loc.location_id}`
  }
  if (loc.subject_id) {
    return `sub-${loc.subject_id}`
  }
  return `${loc.fc_franchisee_no}-${loc.store_number}`
}

function lifecycleBadgeClass(stage) {
  const map = {
    Open: 'badge-success',
    Active: 'badge-success',
    'In Development': 'badge-warning',
    Closed: 'badge-danger',
    Transfers: 'badge-info',
  }
  return map[stage] || 'badge-neutral'
}

function formatDateTime(dateString) {
  if (!dateString) return ''
  return new Date(dateString).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

onMounted(() => {
  fetchData()
})

defineExpose({ refreshData, fetchData })
</script>

<style scoped>
.franchise-locations-panel.embedded .filters-section {
  background: #f8fafc;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
}

.filters-section {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  align-items: flex-end;
  margin-bottom: 1.5rem;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.filter-group label {
  font-size: 0.85rem;
  font-weight: 600;
  color: #475569;
}

.filter-group input,
.filter-group select {
  min-width: 160px;
  padding: 0.5rem 0.75rem;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  background: white;
}

.refresh-btn {
  padding: 0.55rem 1rem;
  border: none;
  border-radius: 8px;
  background: #1e40af;
  color: white;
  font-weight: 600;
  cursor: pointer;
}

.refresh-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.export-btn {
  background: #0f766e;
}

.stats-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.stat-card {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 1rem;
  text-align: center;
}

.stat-card-highlight {
  border-color: #1e40af;
  background: #eff6ff;
}

.stat-value {
  font-size: 1.75rem;
  font-weight: 700;
  color: #1e293b;
}

.stat-label {
  font-size: 0.8rem;
  color: #64748b;
}

.sync-banner {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 999px;
  font-size: 0.875rem;
  margin-bottom: 1rem;
  background: #f1f5f9;
}

.sync-success { background: #dcfce7; color: #166534; }
.sync-error { background: #fee2e2; color: #991b1b; }

.loading-state,
.error-state,
.empty-state {
  text-align: center;
  padding: 2rem 1rem;
  color: #64748b;
}

.table-container {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 1.5rem;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.table-header h2 {
  margin: 0;
  font-size: 1.1rem;
}

.result-count {
  color: #64748b;
  font-size: 0.875rem;
}

.table-wrapper {
  overflow-x: auto;
}

.locations-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
}

.locations-table th,
.locations-table td {
  padding: 0.75rem;
  border-bottom: 1px solid #e2e8f0;
  text-align: left;
}

.locations-table th {
  background: #f8fafc;
  color: #334155;
  font-weight: 600;
}

.store-number {
  font-weight: 700;
  color: #1e40af;
}

.fc-ref {
  font-family: ui-monospace, monospace;
  font-size: 0.85rem;
  color: #64748b;
}

.badge {
  display: inline-block;
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
}

.badge-success { background: #dcfce7; color: #166534; }
.badge-warning { background: #fef3c7; color: #92400e; }
.badge-info { background: #dbeafe; color: #1e40af; }
.badge-danger { background: #fee2e2; color: #991b1b; }
.badge-neutral { background: #f1f5f9; color: #475569; }

.entity-link {
  color: #1e40af;
  font-weight: 600;
  text-decoration: none;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  margin-top: 1.5rem;
}

.page-btn {
  padding: 0.5rem 1rem;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  background: white;
  cursor: pointer;
}

.page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.retry-btn {
  margin-top: 1rem;
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 8px;
  background: #1e40af;
  color: white;
  cursor: pointer;
}
</style>
